/**
 * useRazorpay.js — FIXED
 *
 * Key fix: the handler now logs exactly what Razorpay returns so you
 * can see if razorpay_order_id / razorpay_signature are present.
 * Also added a guard: if order_id is missing from the response,
 * we show a clear error instead of silently sending bad data to verify-payment.
 *
 * Env var needed:
 *   VITE_RAZORPAY_KEY=rzp_live_XXXXXXXX  (or rzp_test_XXXXXXXX for test mode)
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

export const useRazorpay = () => {
  const [isLoaded,  setIsLoaded]  = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const scriptRef = useRef(null);

  useEffect(() => {
    if (window.Razorpay) { setIsLoaded(true); return; }

    setIsLoading(true);
    const script = document.createElement('script');
    script.src   = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload  = () => { setIsLoaded(true);  setIsLoading(false); };
    script.onerror = () => { setIsLoading(false); console.error('[useRazorpay] Failed to load Razorpay SDK'); };
    document.body.appendChild(script);
    scriptRef.current = script;

    return () => {
      if (scriptRef.current) document.body.removeChild(scriptRef.current);
    };
  }, []);

  const openCheckout = useCallback((options = {}) => {
    if (!isLoaded) { console.warn('[useRazorpay] SDK not loaded yet'); return; }

    const {
      amount      = 9900,
      currency    = 'INR',
      name        = 'TypeMyCv',
      description = 'Resume Download',
      image       = '',
      orderId     = undefined,   // MUST be passed — from /api/create-order
      prefill     = {},
      theme       = { color: '#C8973A' },
      onSuccess   = () => {},
      onFailure   = () => {},
    } = options;

    // ── Guard: warn loudly if no orderId — verify-payment WILL fail ──
    if (!orderId) {
      console.error(
        '[useRazorpay] No orderId provided! Razorpay will not return a ' +
        'razorpay_order_id or razorpay_signature without a server-created order. ' +
        'Call /api/create-order first and pass the returned orderId here.'
      );
    }

    const rzpOptions = {
      key        : import.meta.env.VITE_RAZORPAY_KEY,
      amount,
      currency,
      name,
      description,
      image,
      order_id   : orderId,   // ← Razorpay field name is order_id (underscore)
      prefill,
      theme,
      modal: {
        ondismiss: () => onFailure({ description: 'Payment modal closed by user' }),
      },
      handler: (response) => {
        // ── Log exactly what Razorpay returned so we can debug ──
        console.log('[useRazorpay] Razorpay handler response keys:', Object.keys(response));
        console.log('[useRazorpay] razorpay_order_id:', response.razorpay_order_id);
        console.log('[useRazorpay] razorpay_payment_id:', response.razorpay_payment_id);
        console.log('[useRazorpay] razorpay_signature:', response.razorpay_signature ? '✓ present' : '✗ MISSING');

        // ── Guard: if signature or order_id missing, don't call verify ──
        if (!response.razorpay_order_id || !response.razorpay_signature) {
          console.error(
            '[useRazorpay] Razorpay response is missing order_id or signature. ' +
            'This usually means no order_id was set on the checkout options. ' +
            'Received:', response
          );
          onFailure({
            description:
              'Payment completed but order verification data is missing. ' +
              'Please contact support with payment ID: ' + response.razorpay_payment_id,
          });
          return;
        }

        onSuccess(response);
      },
    };

    const rzp = new window.Razorpay(rzpOptions);
    rzp.on('payment.failed', (res) => onFailure(res.error));
    rzp.open();
  }, [isLoaded]);

  return { isLoaded, isLoading, openCheckout };
};